import { z } from 'zod'
import { moneySchema } from './common.js'
import type { Permission } from './permissions.js'

/**
 * Project insights (docs/refactor/projects/05-insights-and-ai.md Part A; P3_BREAKDOWN.md). Signals with
 * typed parameters, health per dimension and overall, earned value and forecasts, computed on read by
 * one pure function (`derive`) from the project's figures. Text is not in the API: the web reads
 * `projectInsights:signal.<CODE>.title` / `.detail` with the params as `{{vars}}`. P5 adds the stored
 * table, the daily job, notifications and acknowledgements; P7 the weekly snapshots.
 */

/**
 * Bumped whenever a formula changes; carried by every result and stored row (P5) and snapshot (P7).
 * 2: forecasts wait for both progress and spending (P3 decision 29); the contract signals (P5).
 * 3: TIME_NOT_SUBMITTED; approved time in the actual cost (P6).
 * 4: TASKS_OVERDUE.
 */
export const INSIGHTS_CALC_VERSION = 4

export const HEALTH_LEVELS = ['ON_TRACK', 'AT_RISK', 'OFF_TRACK', 'NOT_MEASURED'] as const
export type HealthLevel = (typeof HEALTH_LEVELS)[number]

export const SIGNAL_SEVERITIES = ['info', 'warning', 'critical'] as const
export type SignalSeverity = (typeof SIGNAL_SEVERITIES)[number]

/**
 * Overall health is the worst of cost, schedule, billing and contract (critical → OFF_TRACK, warning →
 * AT_RISK); delivery and hygiene raise it to AT_RISK at most (05 §5).
 */
export const HEALTH_DIMENSIONS = ['cost', 'schedule', 'billing', 'contract', 'delivery', 'hygiene'] as const
export type HealthDimension = (typeof HEALTH_DIMENSIONS)[number]

export const SIGNAL_CODES = [
  'BURN_AHEAD',
  'BUDGET_EXCEEDED',
  'CATEGORY_EXCEEDED',
  'FORECAST_LOSS',
  'MARGIN_EROSION',
  'MILESTONE_OVERDUE',
  'SCHEDULE_SLIP',
  'MILESTONE_UNBILLED',
  'TM_UNBILLED',
  'RECEIVABLE_OVERDUE',
  'PROJECT_STALE',
  'TASKS_BLOCKED',
  'TASKS_OVERDUE',
  'UNASSIGNED_COSTS',
  'NO_APPROVED_BUDGET',
  'TIME_NOT_SUBMITTED',
  'RETENTION_DUE',
  'BOND_EXPIRING',
  'CLAIM_NOT_CERTIFIED',
  'VARIATION_PENDING',
  'ADVANCE_RECOVERY_BEHIND',
] as const
export type SignalCode = (typeof SIGNAL_CODES)[number]

/** The workspace tabs a signal's action opens (with optional search params). */
export type SignalActionTab = 'overview' | 'plan' | 'tasks' | 'budget' | 'billing' | 'contract' | 'time'

export interface SignalDef {
  dimension: HealthDimension
  release: 'P3' | 'P5' | 'P6'
  /** Parameter names (the `{{vars}}` of the EN/AR texts). */
  params: readonly string[]
  /** Parameters carry amounts: the signal needs projects.financials (dropped otherwise). */
  money: boolean
  /** Billing signal: also needs projects.bill or sales.read. */
  billing: boolean
  /** Where its one action goes, and who sees the button. */
  action: { tab: SignalActionTab; permission: Permission | null }
}

export const SIGNAL_DEFS: Record<SignalCode, SignalDef> = {
  BURN_AHEAD: {
    dimension: 'cost',
    release: 'P3',
    params: ['spentPct', 'progressPct', 'gapPts'],
    money: true,
    billing: false,
    action: { tab: 'budget', permission: null },
  },
  BUDGET_EXCEEDED: {
    dimension: 'cost',
    release: 'P3',
    params: ['bac', 'ac', 'overPct'],
    money: true,
    billing: false,
    action: { tab: 'budget', permission: 'projects.budgets' },
  },
  CATEGORY_EXCEEDED: {
    dimension: 'cost',
    release: 'P3',
    params: ['category', 'budget', 'actual', 'committed'],
    money: true,
    billing: false,
    action: { tab: 'budget', permission: null },
  },
  FORECAST_LOSS: {
    dimension: 'cost',
    release: 'P3',
    params: ['eac', 'rac', 'loss'],
    money: true,
    billing: false,
    action: { tab: 'budget', permission: null },
  },
  MARGIN_EROSION: {
    dimension: 'cost',
    release: 'P3',
    params: ['baselinePct', 'forecastPct'],
    money: true,
    billing: false,
    action: { tab: 'budget', permission: null },
  },
  MILESTONE_OVERDUE: {
    dimension: 'schedule',
    release: 'P3',
    params: ['milestone', 'plannedOn', 'daysLate'],
    money: false,
    billing: false,
    action: { tab: 'plan', permission: null },
  },
  SCHEDULE_SLIP: {
    dimension: 'schedule',
    release: 'P3',
    params: ['plannedOn', 'forecastOn', 'slipDays', 'spiT'],
    money: false,
    billing: false,
    action: { tab: 'plan', permission: null },
  },
  MILESTONE_UNBILLED: {
    dimension: 'billing',
    release: 'P3',
    params: ['milestone', 'amount', 'doneOn'],
    money: true,
    billing: true,
    action: { tab: 'billing', permission: 'projects.bill' },
  },
  TM_UNBILLED: {
    dimension: 'billing',
    release: 'P3',
    params: ['amount', 'oldestOn', 'hours'],
    money: true,
    billing: true,
    action: { tab: 'billing', permission: 'projects.bill' },
  },
  RECEIVABLE_OVERDUE: {
    dimension: 'billing',
    release: 'P3',
    params: ['amount', 'count', 'maxDays'],
    money: true,
    billing: true,
    action: { tab: 'billing', permission: null },
  },
  PROJECT_STALE: {
    dimension: 'delivery',
    release: 'P3',
    params: ['lastActivityOn', 'days'],
    money: false,
    billing: false,
    action: { tab: 'plan', permission: null },
  },
  TASKS_BLOCKED: {
    dimension: 'delivery',
    release: 'P3',
    params: ['count', 'oldestDays'],
    money: false,
    billing: false,
    action: { tab: 'tasks', permission: null },
  },
  TASKS_OVERDUE: {
    dimension: 'delivery',
    release: 'P3',
    params: ['count', 'oldestDays'],
    money: false,
    billing: false,
    action: { tab: 'tasks', permission: null },
  },
  UNASSIGNED_COSTS: {
    dimension: 'hygiene',
    release: 'P3',
    params: ['count', 'amount'],
    money: true,
    billing: false,
    action: { tab: 'budget', permission: null },
  },
  NO_APPROVED_BUDGET: {
    dimension: 'hygiene',
    release: 'P3',
    params: [],
    money: false,
    billing: false,
    action: { tab: 'budget', permission: 'projects.budgets' },
  },
  TIME_NOT_SUBMITTED: {
    dimension: 'delivery',
    release: 'P6',
    params: ['member', 'weekStart', 'expected', 'submitted'],
    money: false,
    billing: false,
    action: { tab: 'time', permission: null },
  },
  RETENTION_DUE: {
    dimension: 'contract',
    release: 'P5',
    params: ['amount', 'dueOn'],
    money: true,
    billing: true,
    action: { tab: 'contract', permission: 'projects.bill' },
  },
  BOND_EXPIRING: {
    dimension: 'contract',
    release: 'P5',
    params: ['bondType', 'ref', 'expiresOn'],
    money: false,
    billing: false,
    action: { tab: 'contract', permission: null },
  },
  CLAIM_NOT_CERTIFIED: {
    dimension: 'contract',
    release: 'P5',
    params: ['claim', 'submittedOn', 'days', 'amount'],
    money: true,
    billing: true,
    action: { tab: 'contract', permission: null },
  },
  VARIATION_PENDING: {
    dimension: 'contract',
    release: 'P5',
    params: ['variation', 'days', 'amount'],
    money: true,
    billing: false,
    action: { tab: 'contract', permission: null },
  },
  ADVANCE_RECOVERY_BEHIND: {
    dimension: 'contract',
    release: 'P5',
    params: ['recoveredPct', 'progressPct', 'outstanding'],
    money: true,
    billing: true,
    action: { tab: 'contract', permission: null },
  },
}

/** The signals evaluated so far (P3 project signals; P5 contract signals). TIME_NOT_SUBMITTED joins in P6. */
export const SIGNAL_CODES_AVAILABLE: readonly SignalCode[] = SIGNAL_CODES.filter(
  (c) =>
    SIGNAL_DEFS[c].release === 'P3' || SIGNAL_DEFS[c].release === 'P5' || SIGNAL_DEFS[c].release === 'P6',
)

// ── settings (group `projectInsights`) ───────────────────────────────────────

const days = (d: number) => z.number().int().min(0).max(365).default(d)
const pct = (d: number) => z.number().int().min(0).max(100).default(d)

/**
 * Thresholds of the signals (05 §4.3; defaults are product choices, ASM-221). Read: projects.read;
 * write: settings.manage. Keys of later releases join with them.
 */
export const projectInsightsSettingsSchema = z.object({
  /** BURN_AHEAD: spent % − progress % above this many points (critical at twice). */
  burnGapPts: pct(10),
  /** CATEGORY_EXCEEDED: over budget × (1 + this %); "near" from `categoryNearPct`. */
  categoryTolerancePct: pct(0),
  categoryNearPct: pct(90),
  /** MARGIN_EROSION: forecast margin % below the budgeted one by more than this. */
  marginDropPts: pct(5),
  /** MILESTONE_OVERDUE: critical from this many working days late. */
  milestoneCriticalDays: days(10),
  /** SCHEDULE_SLIP: forecast finish later than planned by more than this (critical above `slipCriticalDays`). */
  slipToleranceDays: days(5),
  slipCriticalDays: days(20),
  /** MILESTONE_UNBILLED: an achieved billing milestone without an invoice after this many working days. */
  unbilledGraceDays: days(3),
  /** TM_UNBILLED: the oldest billable unbilled line older than this many days (critical at twice). */
  tmUnbilledDays: days(30),
  /** RECEIVABLE_OVERDUE: project invoices more than this many days past due; critical beyond `arCriticalDays`. */
  arOverdueDays: days(0),
  arCriticalDays: days(60),
  /** PROJECT_STALE: an ACTIVE project with no activity for this many days. */
  staleDays: days(14),
  /** TASKS_BLOCKED: project tasks blocked for more than this many days. */
  blockedDays: days(3),
  /** TASKS_OVERDUE: open project tasks more than this many days past their due date. */
  overdueTaskDays: days(0),
  /** UNASSIGNED_COSTS: only when the untagged amount reaches this (OMR). */
  unassignedMinAmount: moneySchema.default('50.000'),
  /** Below this progress the forecast equals the budget ("Too early to forecast"). */
  minProgressForForecastPct: pct(10),
  /** Estimate at completion (ASM-222): BAC ÷ CPI; AC + (BAC − EV); AC + (BAC − EV) ÷ (CPI × SPI). */
  eacMethod: z.enum(['CPI', 'REMAINING_AT_BUDGET', 'CPI_SPI']).default('CPI'),
  /** RETENTION_DUE: from this many days before a stage's release date (warning once due). */
  retentionLeadDays: days(14),
  /** BOND_EXPIRING: from this many days before a guarantee expires (critical once expired). */
  bondLeadDays: days(30),
  /** CLAIM_NOT_CERTIFIED: submitted and not certified after this many days (critical at twice). */
  claimCertDays: days(14),
  /** VARIATION_PENDING: undecided this many days after submission. */
  variationPendingDays: days(14),
  /** ADVANCE_RECOVERY_BEHIND: recovered % below progress % by more than this many points. */
  advanceBehindPts: pct(10),
  /** TIME_NOT_SUBMITTED (P6): an allocated member submitted less than this % of last week's expected hours. */
  timeSubmitPct: pct(80),
  /** At most this many signal notifications per person per day; the rest become one digest (ASM-227). */
  notifyDailyCap: z.number().int().min(1).max(200).default(20),
})
export type ProjectInsightsSettings = z.output<typeof projectInsightsSettingsSchema>
export const DEFAULT_PROJECT_INSIGHTS_SETTINGS: ProjectInsightsSettings = projectInsightsSettingsSchema.parse(
  {},
)

// ── results ──────────────────────────────────────────────────────────────────

/** A parameter value: numbers and amounts as strings (amounts to 3 decimals), dates `YYYY-MM-DD`. */
export type SignalParam = string | number | null

export interface ProjectSignal {
  code: SignalCode
  severity: SignalSeverity
  dimension: HealthDimension
  /** What inside the project it is about: `project`, a node id, a category id, an item id. */
  scopeKey: string
  params: Record<string, SignalParam>
  /** When it first held at this severity, when known (from the dates of its inputs). */
  since: string | null
  action: { tab: SignalActionTab; search: Record<string, string>; permission: Permission | null }
  /**
   * An acknowledgement silences its notifications until `untilOn` (ASM-217: it still counts in health);
   * an escalation ends it. Null when none is active.
   */
  acknowledged?: {
    by: { id: string; name: string; nameAr: string | null }
    note: string
    untilOn: string
  } | null
}

// ── stored health, acknowledgements, notifications (P5) ──────────────────────

/**
 * `POST /projects/:id/signals/:code/acknowledge` (projects.manage, or the project's manager): silences
 * the signal's notifications until `untilOn` (at most 90 days ahead); 422 SIGNAL_NOT_ACTIVE when the
 * signal doesn't hold now. `DELETE` the same path ends it.
 */
export const signalAcknowledgeSchema = z.object({
  scopeKey: z.string().min(1).max(80),
  note: z.string().trim().min(1, 'Say why').max(500),
  untilOn: z.iso.date(),
})
export type SignalAcknowledgeInput = z.input<typeof signalAcknowledgeSchema>

/**
 * A project's stored health (the daily job and project changes write it; 05 §7.2), as the portfolio
 * shows it. The project page always computes live.
 */
export interface StoredProjectHealth {
  health: HealthLevel
  /** Worst first, at most 3; the rest are counted. */
  topSignals: { code: SignalCode; severity: SignalSeverity }[]
  signalCount: number
  /** Money figures null without projects.financials. */
  forecastMarginPct: string | null
  budgetUsePct: string | null
  calcVersion: number
  computedAt: string
  /** P7: the health of the latest weekly snapshot, for a better/worse mark; null before the first. Optional for fixtures. */
  previousHealth?: HealthLevel | null
}

/** `POST /projects/insights/refresh` (projects.manage): queues the company's run; 202 with when it was queued. */
export interface InsightsRefreshResult {
  queuedAt: string
  /** The previous run's time, if any. */
  lastComputedAt: string | null
}

/** Notification dedupe periods (05 §7.4): the week for persistent signals, countdown marks for dated ones. */
export const SIGNAL_NOTIFY_PERIODS = ['WEEK', 'T30', 'T14', 'T7', 'T0'] as const

export type DimensionLevel = 'none' | SignalSeverity

export interface ProjectMetrics {
  /** 0–100, one decimal, by the project's progress method; null without a plan. */
  progressPct: string | null
  /** Budget at completion: the APPROVED budget's cost; null without one or without financials. */
  bac: string | null
  /** Actual cost to date; null without financials. */
  ac: string | null
  committed: string | null
  /** Earned value = progress × BAC. */
  ev: string | null
  /** Planned value today: each node's budget spread over its working days. */
  pv: string | null
  /** EV ÷ AC; SPI = EV ÷ PV; SPI(t) = earned schedule ÷ actual time (05 §6.2). Two decimals; null when undefined. */
  cpi: string | null
  spi: string | null
  spiT: string | null
  /** Estimate at completion (to complete, variance at completion); equals BAC while `tooEarly`. */
  eac: string | null
  etc: string | null
  vac: string | null
  eacMethod: ProjectInsightsSettings['eacMethod']
  /** Progress below `minProgressForForecastPct`. */
  tooEarly: boolean
  /** Revenue at completion: contract value (+ approved variations later); null for INTERNAL projects. */
  rac: string | null
  /** (RAC − EAC) ÷ RAC × 100 and the budget's own margin %, one decimal. */
  forecastMarginPct: string | null
  baselineMarginPct: string | null
  plannedFinish: string | null
  forecastFinish: string | null
  /** Forecast finish − planned finish in working days (positive: late). */
  slipDays: number | null
  billed: string | null
  collected: string | null
  outstanding: string | null
}

/**
 * `GET /projects/:id/insights` (projects.read; 404 out of scope). Live, read-only. Without
 * projects.financials: money metrics are null, money signals are dropped, and the cost and billing
 * dimensions name only their level.
 */
export interface ProjectInsights {
  asOf: string
  calcVersion: number
  health: HealthLevel
  /** Why NOT_MEASURED: no approved budget, no planned dates, or not ACTIVE (ASM-229). */
  notMeasured: 'NO_BUDGET' | 'NO_DATES' | 'NOT_ACTIVE' | null
  dimensions: Record<HealthDimension, { level: DimensionLevel; because: SignalCode[] }>
  /** Worst first (critical, warning, info), then by code. */
  signals: ProjectSignal[]
  metrics: ProjectMetrics
}

/**
 * `GET /projects/:id/curve` (projects.financials): cumulative monthly figures from the project's start
 * to its end (or today, whichever is later), for the S-curve. PV from the APPROVED budget spread over
 * the plan's working days; EV from the progress history; AC, revenue and collections from the facts.
 */
export interface ProjectCurvePoint {
  /** `YYYY-MM`. */
  month: string
  pv: string | null
  ev: string | null
  ac: string | null
  revenue: string | null
  collections: string | null
}
export interface ProjectCurve {
  asOf: string
  bac: string | null
  points: ProjectCurvePoint[]
  /** The forecast line from today to the forecast finish at EAC; null while too early. */
  forecast: { eac: string; finish: string | null } | null
}

/**
 * The `{{vars}}` each signal's EN/AR texts must use (a locale test compares them with these keys).
 */
export function signalParamKeys(code: SignalCode): readonly string[] {
  return SIGNAL_DEFS[code].params
}

// ── history: weekly snapshots and what changed (P7) ─────────────────────────

/**
 * Snapshot weeks run Sunday to Saturday, Muscat time, for every company (ASM-216): `weekStart` is the
 * Sunday. This is independent of the time grid's `time.weekStart` (P6), which only lays out My time.
 */
export function snapshotWeekStartOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() - d.getUTCDay())
  return d.toISOString().slice(0, 10)
}
/** The Saturday that ends the snapshot week starting `weekStart`. */
export function snapshotWeekEndOf(weekStart: string): string {
  const d = new Date(`${weekStart}T00:00:00Z`)
  d.setUTCDate(d.getUTCDate() + 6)
  return d.toISOString().slice(0, 10)
}

/** How many past weeks the daily job writes when some are missing (the worker was down). */
export const SNAPSHOT_BACKFILL_WEEKS = 8

/**
 * One week of a project, frozen (05 §7.5): figures as of the week's Saturday, as known at `computedAt`.
 * A later posting dated inside the week never rewrites it (ASM-228). Readers get it redacted like live
 * insights: money metrics null and money signals dropped without projects.financials; billing signals
 * dropped without billing access.
 */
export interface ProjectSnapshot {
  weekStart: string
  asOf: string
  health: HealthLevel
  notMeasured: ProjectInsights['notMeasured']
  dimensions: ProjectInsights['dimensions']
  signals: ProjectSignal[]
  metrics: ProjectMetrics
  /** The APPROVED budget version measured against, if any. */
  budgetVersionId: string | null
  calcVersion: number
  computedAt: string
}

/** `GET /projects/:id/insights/history?weeks=` (projects.read in scope): the latest weeks, oldest first. */
export const insightsHistoryQuerySchema = z.object({
  weeks: z.coerce.number().int().min(1).max(104).default(26),
})
export type InsightsHistoryQuery = z.input<typeof insightsHistoryQuerySchema>
export interface ProjectInsightsHistory {
  /** Weeks without a snapshot (the project didn't exist yet, or a gap older than the backfill) are absent. */
  weeks: ProjectSnapshot[]
  /** Where the formulas changed between two consecutive weeks (`calcVersion`), so charts can mark it. */
  calcChanges: { weekStart: string; from: number; to: number }[]
}

/** The project events "What changed" lists (05 §7.6), from the owners' records. */
export const CHANGE_EVENT_KINDS = [
  'STATUS_CHANGED',
  'MILESTONE_ACHIEVED',
  'PROGRESS_UPDATED',
  'BUDGET_APPROVED',
  'VARIATION_DECIDED',
  'CLAIM_CERTIFIED',
  'INVOICE_ISSUED',
  'TIME_APPROVED',
  'MEMBER_ADDED',
] as const
export type ChangeEventKind = (typeof CHANGE_EVENT_KINDS)[number]

/** The figures "What changed" compares; the money ones are null for readers without projects.financials. */
export const CHANGE_FIGURES = [
  'progressPct',
  'ac',
  'committed',
  'eac',
  'forecastMarginPct',
  'forecastFinish',
  'slipDays',
  'billed',
  'collected',
] as const
export type ChangeFigure = (typeof CHANGE_FIGURES)[number]

/**
 * `GET /projects/:id/insights/changes?week=` (projects.read in scope). `week` is a Sunday; default the
 * current week, which compares the live figures with the latest snapshot. A past week compares its own
 * snapshot with the one before. Events come from the week's records, each only if the reader may see it.
 */
export const insightsChangesQuerySchema = z.object({
  week: z.iso
    .date()
    .refine((d) => new Date(`${d}T00:00:00Z`).getUTCDay() === 0, 'A week starts on a Sunday')
    .optional(),
})
export type InsightsChangesQuery = z.input<typeof insightsChangesQuerySchema>
export interface ProjectChanges {
  weekStart: string
  /** The earlier side: the snapshot of the week before; null when there is none (the first week). */
  from: { weekStart: string; computedAt: string; calcVersion: number } | null
  /** The later side: the live figures (the current week) or the week's own snapshot. */
  to: { live: boolean; asOf: string; calcVersion: number }
  health: { before: HealthLevel | null; after: HealthLevel }
  signals: {
    new: ProjectSignal[]
    /** Gone since the earlier side (code, scope and its last severity). */
    resolved: {
      code: SignalCode
      scopeKey: string
      severity: SignalSeverity
      params: Record<string, SignalParam>
    }[]
    escalated: { signal: ProjectSignal; from: SignalSeverity }[]
    eased: { signal: ProjectSignal; from: SignalSeverity }[]
  }
  /** Only the figures that changed. */
  figures: { key: ChangeFigure; before: string | number | null; after: string | number | null }[]
  events: {
    kind: ChangeEventKind
    at: string
    /** What the event is about, when the reader may open it. */
    ref: { type: string; id: string; label: string } | null
    params: Record<string, SignalParam>
    by: { id: string; name: string; nameAr: string | null } | null
  }[]
}
